import { ComponentType, lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
} from 'react-router';

import { AuthGuard, PublicRouteGuard, UniversalRouteGuard } from './auth.guard';
import {
  APP_PAGES,
  privateRoutes,
  publicRoutes,
  universalRoutes,
} from './constants';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import PrivateRoutesContext from '../context/PrivateRoutesContext';
import { CanonicalUrl } from '../core/components/CanonicalUrl';
import { OAuthErrorNotifier } from '../core/components/OAuthErrorNotifier';
import { PageViewTracker } from '../core/components/PageViewTracker';
import { Layout, OnboardingLayout } from '../Layout';
import { BootFallback } from '../Layout/AppShell';
import { OnboardingStep } from '../pages/private/Onboarding/Onboarding.constants';

const Login = lazy(() => import('../pages/public/Login/Login'));
const NotFound = lazy(() => import('../pages/error/NotFound/NotFound'));

export type PageCommonProps = Record<string, string>;

export interface AppRoute {
  path: string;
  element: ComponentType<PageCommonProps>;
}

const isOnboardingRoute = (path: string) => path === APP_PAGES.ONBOARDING;

export const AppRoutes = () => {
  const maintenanceModeEnabled = false;

  if (maintenanceModeEnabled) {
    return createBrowserRouter(
      createRoutesFromElements(
        <Route
          path="*"
          element={<Login />}
          errorElement={<RouteErrorBoundary />}
        />,
      ),
    );
  }

  const onboardingRoutes = privateRoutes.filter((route) =>
    isOnboardingRoute(route.path),
  );
  const layoutRoutes = privateRoutes.filter(
    (route) => !isOnboardingRoute(route.path),
  );

  // CanonicalUrl rides the PUBLIC and UNIVERSAL routes: those are the ones a
  // crawler can reach, and the ones the marketing site links to with a per-page
  // utm_source. Private routes sit behind AuthGuard and are never indexed, so a
  // canonical there would do nothing.
  //
  // BOTH lists, not just this one. /login is public but /signup, /waitlist and
  // /support are universal, and /signup is the single biggest group of
  // duplicates (80 of 149).
  const publicRoutesElements = publicRoutes.map((route) => (
    <Route
      key={route.path}
      path={route.path}
      element={
        <Suspense fallback={<BootFallback />}>
          <CanonicalUrl />
          <PublicRouteGuard>
            <route.element />
          </PublicRouteGuard>
        </Suspense>
      }
    />
  ));

  const universalRoutesElements = universalRoutes.map((route) => (
    <Route
      key={route.path}
      path={route.path}
      element={
        <Suspense fallback={<BootFallback />}>
          <CanonicalUrl />
          <PrivateRoutesContext>
            <UniversalRouteGuard>
              <route.element />
            </UniversalRouteGuard>
          </PrivateRoutesContext>
        </Suspense>
      }
    />
  ));

  return createBrowserRouter(
    createRoutesFromElements(
      <Route errorElement={<RouteErrorBoundary />}>
        {universalRoutesElements}
        {publicRoutesElements}

        <Route
          element={
            <PrivateRoutesContext>
              <AuthGuard>
                <Layout>
                  <PageViewTracker />
                  <OAuthErrorNotifier />
                  <Suspense fallback={<BootFallback />}>
                    <Outlet />
                  </Suspense>
                </Layout>
              </AuthGuard>
            </PrivateRoutesContext>
          }
        >
          {layoutRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<route.element />}
            />
          ))}
        </Route>

        <Route
          element={
            <PrivateRoutesContext>
              <AuthGuard>
                <OnboardingLayout>
                  <PageViewTracker />
                  <OAuthErrorNotifier />
                  <Suspense fallback={<BootFallback />}>
                    <Outlet />
                  </Suspense>
                </OnboardingLayout>
              </AuthGuard>
            </PrivateRoutesContext>
          }
        >
          {onboardingRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<route.element />}
            />
          ))}
        </Route>

        <Route
          path={APP_PAGES.ONBOARDING_BASE}
          element={
            <Navigate
              to={`${APP_PAGES.ONBOARDING_BASE}/${OnboardingStep.WELCOME}`}
              replace={true}
            />
          }
        />
        <Route
          path={APP_PAGES.ROOT}
          element={
            <PrivateRoutesContext>
              <AuthGuard>
                <Navigate to={APP_PAGES.DOBOX} replace={true} />
              </AuthGuard>
            </PrivateRoutesContext>
          }
        />
        <Route
          path="*"
          element={
            <PrivateRoutesContext>
              <AuthGuard>
                <Suspense fallback={<BootFallback />}>
                  <NotFound />
                </Suspense>
              </AuthGuard>
            </PrivateRoutesContext>
          }
        />
      </Route>,
    ),
  );
};
